import React, { createRef, PureComponent } from "react";
import { createSelector } from "reselect";
import { group } from "d3-array";
import Classnames from "classnames";

import "./Card.scss";
import Toggle from "../Toggle";
import VerticalBarChart from "../VerticalBarChart";
import HorizontalBarChart from "../HorizontalBarChart";
import PieChart from "../PieChart";
import WordCloud from "../WordCloud";

import { rawDataQuestion, section } from "../../global-interfaces";

import {
  checkIfMobile,
  generateCardID,
  getURLParams,
  updateURL,
} from "../../utils";
import {
  SUBSET,
  VERT,
  HORIZ,
  PIE,
  WORD,
  EMPHASIS_CHAR,
  SECTION,
  CATEGORY,
  CARD,
  ANNOTATION,
} from "../../constants";

/** INTERFACES */
interface Props {
  data: rawDataQuestion[];
  section: section;
  observer: IntersectionObserver;
  inView: boolean;
  isMobile: boolean;
}

interface State {
  /** tuple that holds the selected category for each position [0, 1, 2]*/
  selectedCategories: (string | null)[];
}

interface positionGroup {
  position: 0 | 1 | 2;
  data: rawDataQuestion;
  categories: (string | null)[];
}

interface ChartTypeProps {
  chartData: rawDataQuestion;
  color: string | null;
  title: boolean;
  toggle: boolean;
  inView: boolean;
}

interface ChartWrapperProps {
  positionGroup: positionGroup;
  setCategory: Function;
  selectedCategory: string | null;
  color: string | null;
  inView: boolean;
  isMobile: boolean;
}

interface AnnotationProps {
  annotation: Array<TextObject>;
}

interface TextObject {
  label: string;
  label_group?: string;
  value?: number;
  column?: number;
}

class DetermineChartType extends PureComponent<ChartTypeProps> {
  render() {
    const { chartData, color, inView } = this.props;

    switch (chartData.chart_type) {
      case SUBSET:
        // console.log("SUBSET COMPONENT", data);
        return (
          <VerticalBarChart chart={chartData} color={color} inView={inView} />
        );

      case VERT:
        // console.log("POPSICLE COMPONENT", data);
        return (
          <VerticalBarChart chart={chartData} color={color} inView={inView} />
        );

      case PIE:
        // console.log("PIE COMPONENT", data);
        return (
          <PieChart
            chart={{
              ...chartData,
              data: chartData.data.sort((a: any, b: any) => b.value - a.value),
            }}
            color={color}
          />
        );

      case HORIZ:
        // console.log("HORIZ COMPONENT", data);
        return (
          <HorizontalBarChart chart={chartData} color={color} inView={inView} />
        );

      case ANNOTATION:
        // console.log("ANNOTATION", chartData);
        return <Annotation annotation={chartData.data} />;

      case WORD:
        // console.log("WORD", chartData);
        return <WordCloud chart={chartData.data.map(d => d.label)} />;
    }
  }
}

class ChartWrapper extends PureComponent<ChartWrapperProps> {
  render() {
    const {
      positionGroup,
      setCategory,
      selectedCategory,
      color,
      inView,
      isMobile,
    } = this.props;
    const { position, data, categories } = positionGroup;
    const title: boolean = !!data.graph_title;
    const toggle: boolean = !!selectedCategory;
    return (
      <div
        className={Classnames(
          "chart-wrapper",
          "pos-" + position,
          data.chart_type
        )}
      >
        {data.graph_title ? (
          <div
            className={Classnames("graph-title", {
              short: data.graph_title.length > (isMobile ? 90 : 200),
            })}
          >
            {<span>{data.graph_title}</span>}
          </div>
        ) : (
          <div />
        )}
        {selectedCategory ? (
          <Toggle
            selectedCategory={selectedCategory as string}
            categories={categories as [string, string]}
            setCategory={(cat: string) => setCategory(position, cat)}
          />
        ) : (
          <div />
        )}
        <DetermineChartType
          chartData={data}
          color={color}
          title={title}
          toggle={toggle}
          inView={inView}
        />
      </div>
    );
  }
}

class Annotation extends PureComponent<AnnotationProps> {
  render() {
    const { annotation } = this.props;
    return (
      <div
        className={Classnames("annotation", {
          "narrow-margin": annotation.length > 500,
        })}
      >
        <div className="content">
          {annotation.map((d, i) => (
            <div key={i} className={d.label_group}>
              {d.label.split(EMPHASIS_CHAR).map((d, i) => (
                <span key={i} className={Classnames({ bold: i % 2 === 1 })}>
                  {d}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }
}
/** SELECTORS */
const getData = (props: Props, _: State) => props.data;
const getSelectedCategories = (_: Props, state: State) =>
  state.selectedCategories;

const positionGroupSelector = createSelector(
  getData,
  //@ts-ignore
  data =>
    Array.from(group(data, d => d.position || 0), ([position, data]) => ({
      position: position,
      data,
      categories: data.map(e => e.category),
    }))
);

const getFilteredPositionGroup = createSelector(
  positionGroupSelector,
  getSelectedCategories,
  (positionGroups, selectedCategories) =>
    positionGroups.map(d => ({
      position: d.position,
      data: d.data.find(
        e => e.category === selectedCategories[d.position]
      ) as rawDataQuestion,
      categories: d.categories,
    }))
);

/** COMPONENT */
class Card extends PureComponent<Props, State> {
  private cardRef: React.RefObject<HTMLDivElement>;
  constructor(props: Props) {
    super(props);
    const positionsGroup = positionGroupSelector(this.props, this.state);
    this.state = {
      selectedCategories: [null, null, null].map((d, i) => {
        const filteredPos = positionsGroup.find(p => p.position === i);
        return filteredPos ? filteredPos.categories[0] : null;
      }),
    };

    this.setCategory = this.setCategory.bind(this);
    this.cardRef = createRef();
  }

  componentDidMount() {
    const { data, section, observer } = this.props;
    const urlParams = getURLParams();
    // attach observer
    if (this.cardRef.current) observer.observe(this.cardRef.current);

    if (
      // 1. see if the urlParams refer to this card
      section.hash === urlParams[SECTION].hash &&
      data[0].card === urlParams[CARD] &&
      urlParams[CATEGORY]
    ) {
      // 2. if so check to see if those categories exist in the data (#failGracefully)
      const positionGroup = positionGroupSelector(this.props, this.state);
      const validCategories = (urlParams[CATEGORY] as []).reduce(
        (a, cat, i) => {
          const curPosition = positionGroup.find(pos => pos.position === i);
          // if that param doesn't exist in possible categories for that position, return false
          return curPosition ? curPosition.categories.includes(cat) && a : a;
        },
        true
      );

      // only setState if categories are valid (ie exist in this Card's data)
      if (validCategories)
        this.setState({
          selectedCategories: urlParams[CATEGORY] as string[],
        });
      else updateURL(section); // clean out URL
    }
  }

  setCategory(pos: 0 | 1 | 2, category: string | null) {
    this.setState(prevState => {
      const selectedCategories = [...prevState.selectedCategories];
      selectedCategories.splice(pos, 1, category);
      return { selectedCategories };
    });
  }

  render() {
    const { selectedCategories } = this.state;
    const { section, inView, isMobile } = this.props;

    const filteredData = getFilteredPositionGroup(this.props, this.state);
    const {
      card,
      card_title,
      annotation,
      section: sectionTitle, // give value an alias
    } = filteredData[0].data;

    return (
      <div
        className={Classnames("Card", {
          "no-toggle": filteredData.reduce(
            (t, v) => t && v.categories.length === 1,
            true
          ),
        })}
        id={generateCardID(section, card)}
        ref={this.cardRef}
      >
        {filteredData.map((d: positionGroup) => (
          <ChartWrapper
            key={d.data.section + "-" + card + "-" + d.position}
            positionGroup={d}
            setCategory={this.setCategory}
            selectedCategory={selectedCategories[d.position]}
            color={section.color}
            inView={inView}
            isMobile={isMobile}
          />
        ))}
      </div>
    );
  }
}

export default Card;
